import Box from 'components/ui/box';
import { IoClose } from 'react-icons/io5';
import { useEffect, useRef } from 'react';

interface Props {
  onClose: () => void;
  description: string;
  isWarning?: boolean;
}

export default function ModalAlert({ onClose, description, isWarning }: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocusedElement.current = document.activeElement as HTMLElement;
    modalRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'Tab') {
        const focusableEls = modalRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusableEls || focusableEls.length === 0) return;
        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement.current?.focus();
    };
  }, [onClose]);

  const titleId = 'modal-alert-title';
  const descId = 'modal-alert-desc';

  return (
    <div
      className="fixed z-20 top-0 left-0 w-full h-full bg-black bg-opacity-80 flex flex-col items-center justify-center"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      tabIndex={-1}
      ref={modalRef}
    >
      <Box className="relative bg-oxford-blue border-electric-blue p-1 lg:p-8 flex flex-col w-[330px] justify-center items-center border-opacity-100 bg-opacity-100">
        <button
          className="absolute -top-5 -right-5  bg-slate-blue rounded-full p-1"
          onClick={() => onClose()}
          aria-label="Fechar modal"
          ref={closeBtnRef}
        >
          <IoClose size={30} className="text-white" />
        </button>

        <div>
          {!isWarning ? (
            <h1 id={titleId} className="text-yellow text-center text-lg mb-4">
              Ops!
            </h1>
          ) : (
            <h1 id={titleId} className="text-yellow text-center text-lg mb-4">
              Olá!
            </h1>
          )}
          <p id={descId}>{description}</p>
        </div>
      </Box>
    </div>
  );
}
